import { useState, useEffect } from 'react';
import { Shield, Save, AlertCircle, Loader2, Check, ExternalLink } from 'lucide-react';
import toast from '../Toast';
import { platformFetch } from '../../platform/http';
import { formatBeijingDateTime } from '../../utils/time';

interface OAuthConfigData {
  linuxdoEnabled: boolean;
  linuxdoClientId: string;
  linuxdoClientSecret: string;
  linuxdoRedirectUri: string;
  linuxdoMinTrustLevel: number;
  updatedAt?: string;
}

export default function OAuthConfigTab() {
  const [config, setConfig] = useState<OAuthConfigData>({
    linuxdoEnabled: false,
    linuxdoClientId: '',
    linuxdoClientSecret: '',
    linuxdoRedirectUri: '',
    linuxdoMinTrustLevel: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState<{ configured: boolean; checks: any } | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await platformFetch('/oauth-config');
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else {
        toast.error('获取配置失败');
      }
    } catch {
      toast.error('获取配置失败');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (config.linuxdoEnabled && (!config.linuxdoClientId.trim() || !config.linuxdoRedirectUri.trim())) {
      toast.error('启用 Linux Do OAuth 时需要填写 Client ID 和 Redirect URI');
      return;
    }

    setSaving(true);
    try {
      const res = await platformFetch('/oauth-config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig((prev) => ({
          ...prev,
          linuxdoClientSecret: data.config.linuxdoClientSecret,
        }));
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        toast.success('配置已保存');
      } else {
        const error = await res.json();
        toast.error(error.error || '保存失败');
      }
    } catch {
      toast.error('保存失败');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await platformFetch('/oauth-config/test', {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setTestResult(data);
        if (data.configured) {
          toast.success('配置完整，可以正常使用');
        } else {
          toast.error('配置不完整，请检查各项设置');
        }
      } else {
        const error = await res.json();
        toast.error(error.error || '测试失败');
      }
    } catch {
      toast.error('测试失败');
    }
    setTesting(false);
  };

  const setField = <K extends keyof OAuthConfigData>(field: K, value: OAuthConfigData[K]) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-2xl shadow-lg border border-light-border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent-blue rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-light-text">Linux Do OAuth 配置</h3>
            <p className="text-sm text-light-muted">配置 Linux Do 第三方登录</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* 启用开关 */}
          <div className="flex items-center justify-between p-4 bg-light-hover rounded-xl">
            <div>
              <p className="font-medium text-light-text">启用 Linux Do 登录</p>
              <p className="text-sm text-light-muted mt-1">开启后用户可以使用 Linux Do 账号登录</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.linuxdoEnabled}
                onChange={(e) => setField('linuxdoEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-blue"></div>
            </label>
          </div>

          {/* Client ID */}
          <div>
            <label className="text-sm font-medium text-light-text mb-1.5 block">
              Client ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.linuxdoClientId}
              onChange={(e) => setField('linuxdoClientId', e.target.value)}
              placeholder="请输入 Linux Do OAuth Client ID"
              className="w-full px-4 py-2.5 rounded-xl border border-light-border bg-light-bg focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            />
          </div>

          {/* Client Secret */}
          <div>
            <label className="text-sm font-medium text-light-text mb-1.5 block">
              Client Secret <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={config.linuxdoClientSecret}
              onChange={(e) => setField('linuxdoClientSecret', e.target.value)}
              placeholder={config.linuxdoClientSecret === '********' ? '已配置，修改请输入新密钥' : '请输入 Linux Do OAuth Client Secret'}
              className="w-full px-4 py-2.5 rounded-xl border border-light-border bg-light-bg focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            />
          </div>

          {/* Redirect URI */}
          <div>
            <label className="text-sm font-medium text-light-text mb-1.5 block">
              Redirect URI <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.linuxdoRedirectUri}
              onChange={(e) => setField('linuxdoRedirectUri', e.target.value)}
              placeholder="例如: https://yourdomain.com/api/oauth/linuxdo/callback"
              className="w-full px-4 py-2.5 rounded-xl border border-light-border bg-light-bg focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            />
          </div>

          {/* Min Trust Level */}
          <div>
            <label className="text-sm font-medium text-light-text mb-1.5 block">
              最低信任等级
            </label>
            <select
              value={config.linuxdoMinTrustLevel}
              onChange={(e) => setField('linuxdoMinTrustLevel', parseInt(e.target.value))}
              className="w-full px-4 py-2.5 rounded-xl border border-light-border bg-light-bg focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            >
              <option value={0}>0 - 新用户</option>
              <option value={1}>1 - 基础用户</option>
              <option value={2}>2 - 活跃用户</option>
              <option value={3}>3 - 核心用户</option>
              <option value={4}>4 - 领袖用户</option>
            </select>
          </div>

          {/* 帮助信息 */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">如何获取 OAuth 配置？</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>访问 <a href="https://connect.linux.do/" target="_blank" rel="noopener noreferrer" className="underline flex items-center gap-1">Linux Do 开发者平台 <ExternalLink className="w-3 h-3 inline" /></a></li>
                  <li>注册并创建 OAuth 应用</li>
                  <li>获取 Client ID 和 Client Secret</li>
                  <li>设置回调地址为上方的 Redirect URI</li>
                </ol>
              </div>
            </div>
          </div>

          {/* 测试配置 */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleTest}
              disabled={testing}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              测试配置
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : saved ? (
                <Check className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? '保存中...' : saved ? '已保存' : '保存配置'}
            </button>
          </div>

          {/* 测试结果 */}
          {testResult && (
            <div className={`p-4 rounded-xl border ${
              testResult.configured
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <p className={`font-medium ${testResult.configured ? 'text-green-700' : 'text-yellow-700'}`}>
                {testResult.configured ? '配置完整' : '配置不完整'}
              </p>
              <div className="mt-2 space-y-1 text-sm">
                <p className={testResult.checks.linuxdoEnabled ? 'text-green-600' : 'text-yellow-600'}>
                  {testResult.checks.linuxdoEnabled ? '✓' : '✗'} 已启用
                </p>
                <p className={testResult.checks.hasClientId ? 'text-green-600' : 'text-yellow-600'}>
                  {testResult.checks.hasClientId ? '✓' : '✗'} Client ID 已配置
                </p>
                <p className={testResult.checks.hasClientSecret ? 'text-green-600' : 'text-yellow-600'}>
                  {testResult.checks.hasClientSecret ? '✓' : '✗'} Client Secret 已配置
                </p>
                <p className={testResult.checks.hasRedirectUri ? 'text-green-600' : 'text-yellow-600'}>
                  {testResult.checks.hasRedirectUri ? '✓' : '✗'} Redirect URI 已配置
                </p>
              </div>
            </div>
          )}

          {/* 更新时间 */}
          {config.updatedAt && (
            <p className="text-xs text-light-muted">
              最后更新：{formatBeijingDateTime(config.updatedAt)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
